/**
 * CL-PROPERTY-OS-002C3 — Portal email verification lifecycle (pure model).
 */

import { PORTAL_PASSWORD_RESET_PATH } from "./portalPasswordReset";

export const PORTAL_LIFECYCLE_PHASE = Object.freeze({
  AUTH: "auth",
  VERIFICATION_REQUIRED: "verification_required",
  VERIFICATION_EMAIL_SENT: "verification_email_sent",
  VERIFICATION_PENDING: "verification_pending",
  VERIFICATION_DELIVERY_FAILED: "verification_delivery_failed",
  SIGNUP_RECOVERY_SETUP: "signup_recovery_setup",
  RECOVERY_INVALID: "recovery_invalid",
  LEGACY_SETUP: "legacy_setup",
  LEGACY_RETRY: "legacy_retry",
  PROPERTY_OS_SETUP: "property_os_setup",
  COMPLETED: "completed",
  PROPERTY_OS_RETRY: "property_os_retry",
  NON_PROPERTY_OS_COMPLETE: "non_property_os_complete",
});

export const VERIFICATION_RESEND_COOLDOWN_MS = 60_000;

export const PORTAL_EMAIL_VERIFICATION_AUTHORIZED_ORIGINS = Object.freeze([
  "https://markbeattie.ca",
  "https://www.markbeattie.ca",
  "http://localhost:3000",
  "http://127.0.0.1:3000",
]);

function safeStr(value) {
  return String(value ?? "").trim();
}

function normalizeOrigin(origin) {
  return safeStr(origin).replace(/\/$/, "").toLowerCase();
}

export function shouldPausePostAuthForEmailVerification(emailVerified) {
  return emailVerified !== true;
}

export function isAuthorizedEmailVerificationContinueOrigin(origin = "") {
  const base = normalizeOrigin(origin);
  if (!base) return false;
  if (PORTAL_EMAIL_VERIFICATION_AUTHORIZED_ORIGINS.map((o) => o.toLowerCase()).includes(base)) {
    return true;
  }
  try {
    const host = new URL(base).hostname.toLowerCase();
    if (host === "localhost" || host === "127.0.0.1") return true;
    if (host.endsWith(".firebaseapp.com") || host.endsWith(".web.app")) return true;
  } catch {
    return false;
  }
  return false;
}

/**
 * Firebase actionCodeSettings for verification continue URL.
 * @param {string} [origin]
 * @param {string} [inviteCode]
 */
export function buildPortalEmailVerificationActionCodeSettings(origin = "", inviteCode = "") {
  const base = safeStr(origin).replace(/\/$/, "");
  if (!base || !isAuthorizedEmailVerificationContinueOrigin(base)) return undefined;
  const code = safeStr(inviteCode);
  const inviteQuery = code ? `?invite=${encodeURIComponent(code)}` : "";
  return {
    url: `${base}${PORTAL_PASSWORD_RESET_PATH}${inviteQuery}`,
    handleCodeInApp: false,
  };
}

export function canResendVerificationEmail(lastSentAtMs, nowMs = Date.now()) {
  if (!lastSentAtMs) return true;
  return nowMs - lastSentAtMs >= VERIFICATION_RESEND_COOLDOWN_MS;
}

export function msUntilResendAllowed(lastSentAtMs, nowMs = Date.now()) {
  if (!lastSentAtMs) return 0;
  const remaining = VERIFICATION_RESEND_COOLDOWN_MS - (nowMs - lastSentAtMs);
  return remaining > 0 ? remaining : 0;
}

export function mapEmailVerificationSendError(error) {
  const code = safeStr(error?.code).toLowerCase();
  if (code === "auth/too-many-requests" || code === "functions/resource-exhausted") {
    return "Too many verification emails were requested. Please wait a few minutes and try again.";
  }
  if (code === "functions/invalid-response") {
    return "We could not confirm the verification email was sent. Please try again in a moment.";
  }
  if (code === "functions/failed-precondition") {
    const message = safeStr(error?.message);
    if (message) return message;
  }
  return "We could not send the verification email right now. Please try again in a moment.";
}

export function shouldShowPrePortalVerificationShell({ user, lifecyclePhase } = {}) {
  if (!user || user.isAnonymous) return false;
  if (user.emailVerified === true) return false;
  return (
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.VERIFICATION_REQUIRED ||
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.VERIFICATION_EMAIL_SENT ||
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.VERIFICATION_PENDING ||
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.VERIFICATION_DELIVERY_FAILED
  );
}

export function shouldShowPrePortalLifecycleShell({ lifecyclePhase } = {}) {
  return (
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.LEGACY_SETUP ||
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.LEGACY_RETRY ||
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.SIGNUP_RECOVERY_SETUP ||
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.RECOVERY_INVALID ||
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.PROPERTY_OS_SETUP ||
    lifecyclePhase === PORTAL_LIFECYCLE_PHASE.PROPERTY_OS_RETRY
  );
}

export function buildSafePendingSignupContext({
  inviteCode = "",
  firstName = "",
  lastName = "",
  phone = "",
  preferredContact = "email",
  intent = {},
} = {}) {
  return {
    inviteCode: safeStr(inviteCode),
    firstName: safeStr(firstName),
    lastName: safeStr(lastName),
    phone: safeStr(phone),
    preferredContact: safeStr(preferredContact) || "email",
    intent: {
      services: Array.isArray(intent.services) ? [...intent.services] : [],
      projectIds: Array.isArray(intent.projectIds) ? [...intent.projectIds] : [],
      clientType: safeStr(intent.clientType),
      primaryUserType: safeStr(intent.primaryUserType || intent.role),
      role: safeStr(intent.role),
      accessScopes: Array.isArray(intent.accessScopes) ? [...intent.accessScopes] : [],
      targetEmailLower: safeStr(intent.targetEmailLower),
    },
  };
}
